import type { BusinessTravelCompany } from "../api/use-companies";

export type CompanySubAccount = NonNullable<BusinessTravelCompany["subAccounts"]>[number];

export type CompanyTableRow =
  | { type: "company"; key: string; company: BusinessTravelCompany; isFirst: boolean }
  | { type: "subAccount"; key: string; subAccount: CompanySubAccount };

/**
 * Tenant linkage for a company row: the tenant of the travel agency owning it.
 * Agencies without a tenant fall into the "Sans tenant" bucket.
 */
export function getCompanyTenantId(company: BusinessTravelCompany): string | null {
  return company.travelAgency?.tenantId ?? null;
}

/** Case-insensitive client-side search on name (display + raw GDS), domain and OSI OIN. */
export function filterCompaniesBySearch(
  companies: BusinessTravelCompany[],
  search: string,
): BusinessTravelCompany[] {
  const query = search.trim().toLowerCase();
  if (!query) {
    return companies;
  }

  return companies.filter((company) => {
    const haystack = [company.displayName, company.rawGdsName, company.domain, company.bluebizIdentifier];
    return haystack.some((value) => value?.toLowerCase().includes(query));
  });
}

/** Flattens companies + their sub-account rows into one virtualizable list. */
export function flattenCompanies(companies: BusinessTravelCompany[]): CompanyTableRow[] {
  const rows: CompanyTableRow[] = [];

  companies.forEach((company, index) => {
    rows.push({ type: "company", key: company.id, company, isFirst: index === 0 });
    for (const subAccount of company.subAccounts ?? []) {
      rows.push({ type: "subAccount", key: `${company.id}:${subAccount.id}`, subAccount });
    }
  });

  return rows;
}
